import React, { useEffect, useState } from "react";
import { usePublicPageSection } from "../../../../hooks/usePublicPageSection";
import { resolveMediaUrl } from "../../../../utils/mediaUrl";
import { useLanguage } from "../../../../context/LanguageContext";
import "./Help.css";

const requireField = (fields, key) => {
  const value = fields?.[key];
  if (value == null || String(value).trim() === "") {
    throw new Error(`Help field "${key}" is missing from the API response.`);
  }
  return value;
};

const requireMediaUrl = (section, groupKey) => {
  const item = section.media?.groups?.[groupKey]?.[0] || null;
  if (!item?.url) {
    throw new Error(`Help media group "${groupKey}" is missing from the API response.`);
  }
  return resolveMediaUrl(item.url);
};

const buildModel = (section) => {
  const f = section.fields || {};
  return {
    badge: requireField(f, "badge"),
    title: requireField(f, "title"),
    plusIcon: requireMediaUrl(section, "icon-plus"),
    minusIcon: requireMediaUrl(section, "icon-minus"),
    faqs: [
      {
        question: requireField(f, "faq1Question"),
        answer: requireField(f, "faq1Answer"),
      },
      {
        question: requireField(f, "faq2Question"),
        answer: requireField(f, "faq2Answer"),
      },
      {
        question: requireField(f, "faq3Question"),
        answer: requireField(f, "faq3Answer"),
      },
      {
        question: requireField(f, "faq4Question"),
        answer: requireField(f, "faq4Answer"),
      },
      {
        question: requireField(f, "faq5Question"),
        answer: requireField(f, "faq5Answer"),
      },
    ],
  };
};

const Help = () => {
  const { language, meta } = useLanguage();
  const [openIndex, setOpenIndex] = useState(0);
  const { model, error, isLoading } = usePublicPageSection("about", "help", buildModel);

  useEffect(() => {
    setOpenIndex(0);
  }, [language, model]);

  const toggleItem = (index) => {
    setOpenIndex((current) => (current === index ? null : index));
  };

  if (isLoading) {
    return (
      <section className="help" dir={meta.dir}>
        <div className="help__container">
          <p style={{ color: "#64748b", fontSize: 14 }}>Loading…</p>
        </div>
      </section>
    );
  }

  if (error || !model) {
    return (
      <section className="help" dir={meta.dir}>
        <div className="help__container">
          <p style={{ color: "#b91c1c", fontSize: 14 }}>
            {error || "Help unavailable."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="help" dir={meta.dir}>
      <div className="help__container">
        <div className="help__header">
          <span className="help__badge">{model.badge}</span>
          <h2 className="help__title">{model.title}</h2>
        </div>

        <div className="help__list">
          {model.faqs.map((item, index) => {
            const isOpen = openIndex === index;

            return (
              <div
                key={item.question}
                className={`help__item ${isOpen ? "help__item--open" : ""}`}
              >
                <button
                  type="button"
                  className="help__trigger"
                  onClick={() => toggleItem(index)}
                  aria-expanded={isOpen}
                >
                  <span className="help__question">{item.question}</span>
                  <span className="help__icon" aria-hidden="true">
                    <img src={isOpen ? model.minusIcon : model.plusIcon} alt="" />
                  </span>
                </button>

                <div
                  className="help__panel"
                  style={{
                    maxHeight: isOpen ? "240px" : "0px",
                  }}
                >
                  <p className="help__answer">{item.answer}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Help;
